import React from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { Modal, Header, Container, Grid, Box, SpaceBetween, Button, Alert, StatusIndicator, Badge, ProgressBar, Table, Tabs, Spinner, Pagination, Select } from "@cloudscape-design/components";
import { notificationsStatsModalStore } from "../stores/notificationsStatsModal";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";

const STATISTIC_CARD_STYLE = { textAlign: "center", padding: "15px", background: "#f8f9fa", borderRadius: "8px" };
const STATISTICS_GRID_DEFINITION = Array.from({ length: 8 }, () => ({ colspan: { default: 12, xs: 6, s: 2 } }));

class NotificationsStatsModal extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			statistics: null,
			error: null,
			activeTab: "overview",
			dailyCurrentPage: 1,
			dailyPageSize: 10,
			usersCurrentPage: 1,
			usersPageSize: 10,
			dailyLoading: false,
			usersLoading: false,
			usersStatsExportDateFrom: null,
			usersStatsExportDateTo: null,
		};
	}

	handleExportToCsv = () => {
		this.setState({ loading: true });
		const { data } = this.props.modal;
		this.props.Interface.request(
			"set",
			{ class: "data", module: "notification", method: "exportToCsv" },
			{
				id: data.id,
				usersStatsExportDateFrom: this.state.usersStatsExportDateFrom,
				usersStatsExportDateTo: this.state.usersStatsExportDateTo,
			},
		)
			.then((response) => {
				const blob = new Blob([response.csv], { type: "text/csv;charset=utf-8;" });
				const link = document.createElement("a");
				link.href = URL.createObjectURL(blob);
				link.download = response.filename;
				link.click();
				URL.revokeObjectURL(link.href);
				this.setState({ loading: false });
			})
			.catch((error) => {
				this.setState({
					error: "Failed to export promo codes to CSV",
					loading: false,
					dailyLoading: false,
					usersLoading: false,
				});
			});
	};

	componentDidMount() {
		if (this.props.modal.show && this.props.modal.data) {
			this.loadStats(1, 1, true);
		}
	}

	componentDidUpdate(prevProps) {
		if (this.props.modal.show && !prevProps.modal.show && this.props.modal.data) {
			this.setState({
				dailyCurrentPage: 1,
				usersCurrentPage: 1,
			});
			this.loadStats(1, 1, true);
		}
	}

	loadStats = async (dailyPage = 1, usersPage = 1, isInitialLoad = false) => {
		if (isInitialLoad) {
			this.setState({ loading: true, error: null });
		} else {
			if (dailyPage !== this.state.dailyCurrentPage) {
				this.setState({ dailyLoading: true });
			}
			if (usersPage !== this.state.usersCurrentPage) {
				this.setState({ usersLoading: true });
			}
		}

		try {
			const response = await this.props.Interface.request(
				"get",
				{
					class: "data",
					module: "notification",
					method: "statistics",
				},
				{
					notificationId: this.props.modal.data.id,
					dailyPage: dailyPage || this.state.dailyCurrentPage,
					dailyLimit: this.state.dailyPageSize,
					usersPage: usersPage || this.state.usersCurrentPage,
					usersLimit: this.state.usersPageSize,
				},
			);

			const overall = response.overall || {};

			// Transform the response to match expected structure
			const statistics = {
				overall: {
					...overall,
					totalTargetClicks: overall.totalTargetClicks || 0,
					totalBannerClicks: overall.totalBannerClicks || 0,
					totalButtonClicks: overall.totalButtonClicks || 0,
				},
				daily: response.daily.data,
				dailyPagination: {
					totalDays: response.daily.totalDays,
					totalPages: response.daily.totalPages,
					currentPage: response.daily.currentPage,
					pageSize: response.daily.pageSize,
				},
				users: response.users.data,
				usersPagination: {
					totalUsers: response.users.totalUsers,
					totalPages: response.users.totalPages,
					currentPage: response.users.currentPage,
					pageSize: response.users.pageSize,
				},
			};

			this.setState({
				statistics: statistics,
				loading: false,
				dailyLoading: false,
				usersLoading: false,
				dailyCurrentPage: dailyPage || this.state.dailyCurrentPage,
				usersCurrentPage: usersPage || this.state.usersCurrentPage,
			});
		} catch (error) {
			console.error("Error loading statistics:", error);
			this.setState({
				error: error.message || "Failed to load statistics",
				loading: false,
				dailyLoading: false,
				usersLoading: false,
			});
		}
	};

	handleClose = () => {
		this.props.setModal({
			...this.props.modal,
			show: false,
			data: null,
		});
	};

	formatDate = (dateString) => {
		return new Date(dateString).toLocaleDateString("ru-RU");
	};

	formatDateTime = (dateString) => {
		if (!dateString) return "-";
		return new Date(dateString).toLocaleString("ru-RU");
	};

	getNotificationTitle = (item) => {
		return item.localName || `Notification #${item.id}`;
	};

	getStatusBadge = (status) => {
		const statusMap = {
			queue: { label: "Queue", color: "grey" },
			send: { label: "Sent Not Read", color: "blue" },
			read: { label: "Sent Read", color: "green" },
			closed: { label: "Closed", color: "red" },
			expired: { label: "Sent Failed", color: "red" },
		};

		const config = statusMap[status] || { label: status, color: "grey" };
		return <Badge color={config.color}>{config.label}</Badge>;
	};

	renderStatisticCard = (value, label, color) => (
		<div style={STATISTIC_CARD_STYLE}>
			<Box variant="h2" color={color}>
				{value || 0}
			</Box>
			<Box variant="small" color="text-label">
				{label}
			</Box>
		</div>
	);

	render() {
		const { modal } = this.props;
		const item = modal.data;
		const { statistics, loading, error } = this.state;

		if (!item) return null;

		return (
			<Modal
				visible={modal.show}
				onDismiss={this.handleClose}
				header={`Statistics: ${this.getNotificationTitle(item)}`}
				size="large"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button onClick={this.handleClose}>Close</Button>
						</SpaceBetween>
					</Box>
				}>
				<SpaceBetween size="l">
					{error && (
						<Alert type="error" header="Error">
							{error}
						</Alert>
					)}

					{loading ? (
						<Container>
							<div style={{ textAlign: "center", padding: "40px" }}>
								<Spinner size="large" />
								<Box margin={{ top: "s" }}>Loading statistics...</Box>
							</div>
						</Container>
					) : (
						<Tabs
							activeTabId={this.state.activeTab}
							onChange={({ detail }) => this.setState({ activeTab: detail.activeTabId })}
							tabs={[
								{
									label: "Overview",
									id: "overview",
									content: (
										<SpaceBetween size="l">
											{/* Basic Notification Information */}
											<Container header={<Header variant="h2">Notification Details</Header>}>
												<Grid gridDefinition={[{ colspan: { default: 12, xs: 6 } }, { colspan: { default: 12, xs: 6 } }]}>
													<SpaceBetween size="m">
														<div>
															<Box variant="small" color="text-label">
																Created Date
															</Box>
															<Box>{this.formatDateTime(item.createdAt)}</Box>
														</div>
														<div>
															<Box variant="small" color="text-label">
																Status
															</Box>
															<Box variant="h3">
																<Badge color={item.active ? "green" : "red"}>{item.active ? "Active" : "Inactive"}</Badge>
															</Box>
														</div>
														<div>
															<Box variant="small" color="text-label">
																Priority
															</Box>
															<Box variant="h3">
																<Badge color={item.priority === 5 ? "green" : item.priority >= 3 ? "blue" : "grey"}>Priority {item.priority || 0}</Badge>
															</Box>
														</div>
													</SpaceBetween>
													<SpaceBetween size="m">
														<div>
															<Box variant="small" color="text-label">
																Active Period
															</Box>
															<Box>
																{this.formatDate(item.from)} - {this.formatDate(item.to)}
															</Box>
														</div>
														<div>
															<Box variant="small" color="text-label">
																Page
															</Box>
															<Box variant="h3" style={{ fontWeight: "bold" }}>
																{item.showSettingsConditions ? JSON.parse(item.showSettingsConditions).page : "N/A"}
															</Box>
														</div>
													</SpaceBetween>
												</Grid>
											</Container>

											{/* Live Statistics */}
											<Container header={<Header variant="h2">Live Statistics</Header>}>
												{statistics?.overall ? (
													<SpaceBetween size="m">
														{/* Status counters */}
														<Grid gridDefinition={STATISTICS_GRID_DEFINITION}>
															{this.renderStatisticCard(statistics.overall.totalNotifications, "Sent", "text-status-info")}
															{this.renderStatisticCard(statistics.overall.queueCount, "Queue", "text-status-inactive")}
															{this.renderStatisticCard(statistics.overall.sendCount, "Sent Not Read", "text-status-info")}
															{this.renderStatisticCard(statistics.overall.readCount, "Sent Read", "text-status-success")}
															{this.renderStatisticCard(statistics.overall.expiredCount, "Sent Failed", "text-status-error")}
															{this.renderStatisticCard(statistics.overall.totalTargetClicks, "All target clicks", "text-status-success")}
															{this.renderStatisticCard(statistics.overall.totalBannerClicks, "Banner target clicks", "text-status-success")}
															{this.renderStatisticCard(statistics.overall.totalButtonClicks, "Button target clicks", "text-status-success")}
														</Grid>

														{/* Additional metrics */}
														<SpaceBetween size="m" direction="vertical">
															<div>
																<Box variant="small" color="text-label">
																	Unique Users
																</Box>
																<Box variant="h3">{statistics.overall.uniqueUsers || 0}</Box>
															</div>
															<div>
																<Box variant="small" color="text-label">
																	Conversion Rate (CTR)
																</Box>
																<ProgressBar value={statistics.overall.conversionRate || 0} description={`${statistics.overall.readCount || 0} of ${statistics.overall.totalNotifications || 0} total notifications were read`} />
															</div>
														</SpaceBetween>
													</SpaceBetween>
												) : (
													<Box textAlign="center" padding="l">
														<Box variant="p" color="text-body-secondary">
															No statistics available yet
														</Box>
													</Box>
												)}
											</Container>
										</SpaceBetween>
									),
								},
								{
									label: "Daily Stats",
									id: "daily",
									content:
										statistics?.daily && statistics.daily.length > 0 ? (
											<div
												style={{
													position: "relative",
													opacity: this.state.dailyLoading ? 0.5 : 1,
													transition: "opacity 0.2s",
												}}>
												{this.state.dailyLoading && (
													<div
														style={{
															position: "absolute",
															top: "50%",
															left: "50%",
															transform: "translate(-50%, -50%)",
															zIndex: 10,
														}}>
														<Spinner size="large" />
													</div>
												)}
												<Table
													header={
														<Header
															variant="h2"
															counter={statistics?.dailyPagination ? `(${statistics.dailyPagination.totalDays})` : ""}
															description={statistics?.dailyPagination && statistics.dailyPagination.totalPages > 1 ? `Showing ${(statistics.dailyPagination.currentPage - 1) * statistics.dailyPagination.pageSize + 1}-${Math.min(statistics.dailyPagination.currentPage * statistics.dailyPagination.pageSize, statistics.dailyPagination.totalDays)} of ${statistics.dailyPagination.totalDays}` : null}
															actions={
																<Select
																	selectedOption={{ label: `${this.state.dailyPageSize} per page`, value: this.state.dailyPageSize.toString() }}
																	onChange={({ detail }) => {
																		const newPageSize = parseInt(detail.selectedOption.value);
																		this.setState({ dailyPageSize: newPageSize, dailyCurrentPage: 1 }, () => {
																			this.loadStats(1, this.state.usersCurrentPage);
																		});
																	}}
																	options={[
																		{ label: "10 per page", value: "10" },
																		{ label: "20 per page", value: "20" },
																		{ label: "50 per page", value: "50" },
																		{ label: "100 per page", value: "100" },
																	]}
																/>
															}>
															Daily Statistics
														</Header>
													}
													items={statistics.daily}
													columnDefinitions={[
														{
															id: "date",
															header: "Date",
															cell: (item) => this.formatDate(item.date),
														},
														{
															id: "notifications",
															header: "Total",
															cell: (item) => item.notifications,
														},
														{
															id: "uniqueUsers",
															header: "Unique Users",
															cell: (item) => item.uniqueUsers,
														},
														{
															id: "sendCount",
															header: "Sent",
															cell: (item) => <Badge color="blue">{item.sendCount}</Badge>,
														},
														{
															id: "readCount",
															header: "Read",
															cell: (item) => <Badge color="green">{item.readCount}</Badge>,
														},
														{
															id: "expiredCount",
															header: "Expired",
															cell: (item) => <Badge color="red">{item.expiredCount}</Badge>,
														},
														{
															id: "ctr",
															header: "CTR",
															cell: (item) => {
																const ctr = item.sendCount > 0 ? Math.round((item.readCount / item.sendCount) * 100) : 0;
																return `${ctr}%`;
															},
														},
													]}
													stickyHeader
													empty={
														<Box textAlign="center" padding="l">
															No daily statistics available
														</Box>
													}
													pagination={
														statistics.dailyPagination && statistics.dailyPagination.totalPages > 1 ? (
															<Pagination
																currentPageIndex={statistics.dailyPagination.currentPage}
																onChange={({ detail }) => {
																	this.loadStats(detail.currentPageIndex, this.state.usersCurrentPage);
																}}
																pagesCount={statistics.dailyPagination.totalPages}
																ariaLabels={{
																	nextPageLabel: "Next page",
																	previousPageLabel: "Previous page",
																	pageLabel: (pageNumber) => `Page ${pageNumber}`,
																}}
															/>
														) : null
													}
												/>
											</div>
										) : (
											<Container header={<Header variant="h2">Daily Statistics</Header>}>
												<Box textAlign="center" padding="l">
													<Box variant="p" color="text-body-secondary">
														No daily statistics available yet
													</Box>
												</Box>
											</Container>
										),
								},
								{
									label: "User Details",
									id: "users",
									content:
										statistics?.users && statistics.users.length > 0 ? (
											<div
												style={{
													position: "relative",
													opacity: this.state.usersLoading ? 0.5 : 1,
													transition: "opacity 0.2s",
												}}>
												{this.state.usersLoading && (
													<div
														style={{
															position: "absolute",
															top: "50%",
															left: "50%",
															transform: "translate(-50%, -50%)",
															zIndex: 10,
														}}>
														<Spinner size="large" />
													</div>
												)}
												<Table
													header={
														<Header
															variant="h2"
															counter={statistics?.usersPagination ? `(${statistics.usersPagination.totalUsers})` : ""}
															description={statistics?.usersPagination && statistics.usersPagination.totalPages > 1 ? `Showing ${(statistics.usersPagination.currentPage - 1) * statistics.usersPagination.pageSize + 1}-${Math.min(statistics.usersPagination.currentPage * statistics.usersPagination.pageSize, statistics.usersPagination.totalUsers)} of ${statistics.usersPagination.totalUsers}` : null}
															actions={
																<Select
																	selectedOption={{ label: `${this.state.usersPageSize} per page`, value: this.state.usersPageSize.toString() }}
																	onChange={({ detail }) => {
																		const newPageSize = parseInt(detail.selectedOption.value);
																		this.setState({ usersPageSize: newPageSize, usersCurrentPage: 1 }, () => {
																			this.loadStats(this.state.dailyCurrentPage, 1);
																		});
																	}}
																	options={[
																		{ label: "10 per page", value: "10" },
																		{ label: "20 per page", value: "20" },
																		{ label: "50 per page", value: "50" },
																		{ label: "100 per page", value: "100" },
																	]}
																/>
															}>
															User Notifications
														</Header>
													}
													filter={
														<Box>
															<SpaceBetween direction="horizontal" size="xl">
																<DateRangePicker
																	onChange={({ detail }) => {
																		this.setState({
																			usersStatsExportDateFrom: detail.value.startDate.split("+")[0],
																			usersStatsExportDateTo: detail.value.endDate.split("+")[0],
																		});
																	}}
																	value={{
																		startDate: this.state.usersStatsExportDateFrom,
																		endDate: this.state.usersStatsExportDateTo,
																		type: "absolute",
																	}}
																	relativeOptions={[]}
																	isValidRange={(range) => {
																		if (range.type === "absolute") {
																			const [startDateWithoutTime] = range.startDate.split("T");
																			const [endDateWithoutTime] = range.endDate.split("T");
																			if (!startDateWithoutTime || !endDateWithoutTime) {
																				return {
																					valid: false,
																					errorMessage: "The selected date range is incomplete.",
																				};
																			}
																			if (new Date(range.startDate) - new Date(range.endDate) > 0) {
																				return {
																					valid: false,
																					errorMessage: "Start date must be before end date.",
																				};
																			}
																		}
																		return { valid: true };
																	}}
																	i18nStrings={{
																		todayAriaLabel: "Today",
																		nextMonthAriaLabel: "Next month",
																		previousMonthAriaLabel: "Previous month",
																		customRelativeRangeDurationLabel: "Duration",
																		customRelativeRangeDurationPlaceholder: "Enter duration",
																		customRelativeRangeOptionLabel: "Custom range",
																		customRelativeRangeOptionDescription: "Define a custom range",
																		customRelativeRangeUnitLabel: "Unit of time",
																		formatRelativeRange: ({ startDate, endDate }) => {
																			const start = new Date(startDate).toLocaleString("en-GB");
																			const end = new Date(endDate).toLocaleString("en-GB");
																			return `${start} - ${end}`;
																		},
																		formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
																		dateTimeConstraintText: "Date and time must be within range.",
																		renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
																		labelRelativeRange: "Relative range",
																		labelAbsoluteRange: "Absolute range",
																		dateTimePickerAriaLabel: "Date and time picker",
																		dateTimePickerLabel: "Date and Time",
																		clearButtonAriaLabel: "Clear date range",
																		relativeModeTitle: "Relative mode",
																		absoluteModeTitle: "Absolute mode",
																		cancelButtonLabel: "Cancel",
																		applyButtonLabel: "Apply",
																	}}
																/>
																<Button onClick={this.handleExportToCsv} disabled={this.state.usersStatsExportDateFrom === null || this.state.usersStatsExportDateTo === null}>
																	Export
																</Button>
															</SpaceBetween>
														</Box>
													}
													items={statistics.users}
													columnDefinitions={[
														{
															id: "uid",
															header: "User ID",
															cell: (item) => item.uid,
														},
														{
															id: "status",
															header: "Status",
															cell: (item) => this.getStatusBadge(item.status),
														},
														{
															id: "createdAt",
															header: "Created",
															cell: (item) => this.formatDateTime(item.createdAt),
														},
														{
															id: "start_time",
															header: "Start Time",
															cell: (item) => (item.start_time ? this.formatDateTime(item.start_time) : "-"),
														},
														{
															id: "end_time",
															header: "End Time",
															cell: (item) => (item.end_time ? this.formatDateTime(item.end_time) : "-"),
														},
													]}
													stickyHeader
													empty={
														<Box textAlign="center" padding="l">
															No user notifications yet
														</Box>
													}
													pagination={
														statistics.usersPagination && statistics.usersPagination.totalPages > 1 ? (
															<Pagination
																currentPageIndex={statistics.usersPagination.currentPage}
																onChange={({ detail }) => {
																	this.loadStats(this.state.dailyCurrentPage, detail.currentPageIndex);
																}}
																pagesCount={statistics.usersPagination.totalPages}
																ariaLabels={{
																	nextPageLabel: "Next page",
																	previousPageLabel: "Previous page",
																	pageLabel: (pageNumber) => `Page ${pageNumber}`,
																}}
															/>
														) : null
													}
												/>
											</div>
										) : (
											<Container header={<Header variant="h2">User Notifications</Header>}>
												<Box textAlign="center" padding="l">
													<Box variant="p" color="text-body-secondary">
														No user notifications yet
													</Box>
												</Box>
											</Container>
										),
								},
							]}
						/>
					)}
				</SpaceBetween>
			</Modal>
		);
	}
}

function NotificationsStatsModalWithRecoil(props) {
	const modal = useRecoilValue(notificationsStatsModalStore);
	const setModal = useSetRecoilState(notificationsStatsModalStore);
	return <NotificationsStatsModal {...props} modal={modal} setModal={setModal} Interface={props.Interface} />;
}

export default NotificationsStatsModalWithRecoil;
